import { BOOKING_STATUS } from '@/types/service';
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { ScheduleGrade } from '@/components/schedule/schedule-grade';
import { useScheduleGrade } from '@/components/schedule/use-schedule-grade';
import { ServiceCoverCarousel } from '@/components/schedule/service-cover-carousel';
import { LOCATION_MODE } from '@/types/location-mode';
import { GuestBookingSheet } from '@/components/schedule/guest-booking-sheet';
import { GuestRecurrenceSheet } from '@/components/schedule/guest-recurrence-sheet';
import { SubscriptionPlanCard } from '@/components/schedule/subscription-plan-card';
import { PublicBrandHeader } from '@/components/public/public-brand-header';
import { PublicBrandFooter } from '@/components/public/public-brand-footer';
import { ProfessionalCards } from '@/components/public-profile/professional-cards';
import { getFirstName } from '@/components/team/member-avatar';
import {
  usePublicAvailability,
  usePublicServiceDetail,
  usePublicTeamAvailability,
} from '@/hooks/use-public-schedule';
import { usePreviewPublicRecurrence } from '@/hooks/use-public-recurrence';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { useToastStore } from '@/store/toastStore';
import { formatCurrency } from '@/utils/format-currency';
import { formatDuration, formatSlotTime } from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';
import { SeoHead } from '@/components/seo-head';
import { usePublicProfileView } from '@/hooks/use-public-profile';
import { pageTitle } from '@/lib/seo/meta';
import { memberServices, profileServices, serviceMeta } from '@/lib/seo/profile-meta';

const DEFAULT_BRAND = '#FD5001';
const HORIZON_DAYS = 30;

/** Linha de detalhe do card "Sobre o serviço": rótulo à esquerda, valor à direita. */
function DetailRow({
  icon,
  label,
  value,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  value: string;
}) {
  return (
    <View className="flex-row items-center gap-3">
      <View className="h-8 w-8 items-center justify-center rounded-xl bg-neutral-100">
        <Ionicons className="text-neutral-600" name={icon} size={16} />
      </View>
      <ThemedText className="text-sm text-neutral-500">{label}</ThemedText>
      <ThemedText
        className="flex-1 text-right font-semibold text-sm text-neutral-700"
        numberOfLines={2}
      >
        {value}
      </ThemedText>
    </View>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <View className="flex-1 items-center justify-center gap-3 bg-neutral-50 p-8">{children}</View>
  );
}

/**
 * Página pública do serviço (guest booking): mesma experiência da página
 * logada, mas sem exigir conta — o convidado se identifica por nome+telefone.
 */
export default function PublicServiceBookingScreen() {
  const th = useThemedHex();
  const {
    handle,
    serviceId: serviceParam,
    professional,
  } = useLocalSearchParams<{
    handle: string;
    serviceId: string;
    /** Profissional da equipe dono do serviço; ausente = dono do slug. */
    professional?: string;
  }>();
  const raw = typeof handle === 'string' ? handle : '';
  const slug = raw.startsWith('@') ? raw.slice(1) : raw;
  const professionalParam = typeof professional === 'string' ? professional : null;
  const { addToast } = useToastStore();

  // Na equipe, trocar de profissional troca o serviço pela cópia dele, sem
  // sair da tela.
  const [target, setTarget] = useState({
    serviceId: serviceParam,
    professionalId: professionalParam,
  });
  // Link sem profissional abre em "qualquer": a agenda é a da equipe inteira e
  // a pessoa sai junto com o horário.
  const [anyProfessional, setAnyProfessional] = useState(!professionalParam);
  useEffect(() => {
    setTarget({ serviceId: serviceParam, professionalId: professionalParam });
    setAnyProfessional(!professionalParam);
  }, [serviceParam, professionalParam]);
  const { serviceId, professionalId } = target;

  const {
    data: service,
    isLoading,
    error,
  } = usePublicServiceDetail(slug, serviceId, professionalId);
  const previewRecurrence = usePreviewPublicRecurrence(slug, professionalId);
  // O perfil (já em cache quando se vem da página dele) dá o título e a
  // descrição: os mesmos que a função da Vercel escreve no HTML.
  const { data: profile } = usePublicProfileView(slug);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);

  const isSubscription = !!service?.isSubscription;
  const askAddress = service?.locationMode === LOCATION_MODE.CLIENT_ADDRESS;
  // Assinatura é uma só: o convidado paga o ciclo e marca dentro da cota. Aqui
  // ele nunca é assinante ainda — quem já assinou marca pelo app, logado.
  const quotaTerms = service?.quotaTerms ?? null;

  // "Com quem?": as cópias do serviço na equipe, na ordem dela.
  const professionals = service?.professionals ?? [];
  const hasTeam = professionals.length >= 2;
  // A assinatura monta uma agenda que se repete, sempre com a mesma pessoa:
  // ali a escolha vem antes do horário.
  const anyMode = anyProfessional && hasTeam && !isSubscription;

  // Montar a agenda que se repete é conveniência de quem ainda vai assinar: ele
  // entra no plano já com as sessões pré-marcadas. Depois de assinante, marca
  // cada uma quando quiser — nunca precisa marcar tudo de uma vez. Em
  // "qualquer" não dá para repetir: a repetição é com uma pessoa.
  const canRepeat = !anyMode && (isSubscription || service?.recurringBookingMode === 'AUTO');

  // A mesma agenda do prestador (tela de contrato): repetir por dia+hora,
  // liberar uma data, trocar por outra, tudo dentro da cota do plano.
  const grade = useScheduleGrade({
    serviceId,
    preview: previewRecurrence.mutateAsync,
    canRepeat,
    isSubscription,
    quota: quotaTerms?.sessions ?? null,
    quotaPeriod: quotaTerms?.period ?? QUOTA_PERIOD.MONTH,
    singleWhenOnce: true,
    onWarn: (message) => addToast(message, 'warning'),
  });
  const selected = grade.selected;

  const range = useMemo(() => {
    const from = new Date();
    const to = new Date(from.getTime() + HORIZON_DAYS * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const brand = service?.brandColor || DEFAULT_BRAND;

  const bookingOn = service?.bookingStatus === BOOKING_STATUS.BOOKABLE;
  const soloAvailability = usePublicAvailability({
    slug,
    serviceId: bookingOn && !anyMode ? serviceId : undefined,
    professionalId,
    from: range.from,
    to: range.to,
  });
  const teamAvailability = usePublicTeamAvailability({
    slug,
    serviceId: bookingOn ? serviceId : undefined,
    from: range.from,
    to: range.to,
    enabled: anyMode,
  });
  const availability = anyMode ? teamAvailability : soloAvailability;

  const selectedProviderId = anyMode ? null : (professionalId ?? service?.providerId ?? null);
  const selectedProfessional = professionals.find((p) => p.providerId === selectedProviderId);

  // "Qualquer" volta para a agenda da equipe; uma pessoa troca o serviço
  // pela cópia dela. Nos dois casos o horário escolhido antes cai.
  const chooseProfessional = (providerId: string | null) => {
    if (!providerId) {
      if (anyMode) return;
      setAnyProfessional(true);
      grade.clear();
      return;
    }
    const option = professionals.find((p) => p.providerId === providerId);
    if (!option || (!anyMode && option.providerId === selectedProviderId)) return;
    setAnyProfessional(false);
    setTarget({ serviceId: option.serviceId, professionalId: option.providerId });
    grade.clear();
  };

  // O outro sentido: escolheu o horário primeiro e agora diz com quem — o
  // horário continua escolhido, a agenda vira a dessa pessoa.
  const chooseForSlot = (providerId: string | null) => {
    const option = professionals.find((p) => p.providerId === providerId);
    if (!option) return;
    setAnyProfessional(false);
    setTarget({ serviceId: option.serviceId, professionalId: option.providerId });
  };

  // Quem está livre no horário escolhido, na ordem da equipe.
  const freeAtSlot =
    anyMode && selected
      ? professionals.filter((p) =>
          (selected.professionals ?? []).some((free) => free.providerId === p.providerId),
        )
      : [];

  if (isLoading) {
    return (
      <Centered>
        <ActivityIndicator size="large" color={DEFAULT_BRAND} />
      </Centered>
    );
  }

  if (error || !service) {
    return (
      <Centered>
        <SeoHead title={pageTitle('Serviço indisponível')} noindex />
        <Ionicons className="text-neutral-400" name="alert-circle-outline" size={48} />
        <ThemedText className="text-center font-bold text-xl">Serviço indisponível</ThemedText>
        <ThemedText className="text-center text-neutral-500">
          Este serviço não existe ou não está mais disponível.
        </ThemedText>
        <PressableScale
          onPress={() => router.back()}
          className="mt-2 rounded-full px-6 py-3"
          style={{ backgroundColor: brand }}
        >
          <ThemedText className="font-bold text-white">Voltar</ThemedText>
        </PressableScale>
      </Centered>
    );
  }

  const metaService =
    (profile &&
      (profileServices(profile).find((item) => item.id === serviceId) ??
        (profile.team ?? [])
          .flatMap((member) => memberServices(profile, member))
          .find((item) => item.id === serviceId))) ||
    null;
  const meta = profile
    ? serviceMeta(profile, metaService ?? { ...service, id: serviceId })
    : null;

  const priceLabel =
    service.priceDisplayMode === 'hidden' || service.price == null
      ? 'Valor sob consulta'
      : service.priceDisplayMode === 'from'
        ? `a partir de ${formatCurrency(service.price)}`
        : formatCurrency(service.price);

  const days = availability.data?.days ?? [];
  const hasSlots = days.some((d) => d.available && d.slots.length > 0);
  const hasPhotos = (service.photos?.length ?? 0) > 0;

  const goBack = () => (router.canGoBack() ? router.back() : router.replace(`/@${slug}` as any));

  // A página da pessoa dentro da loja: foto, apresentação, os serviços dela e
  // o portfólio dela.
  const openProfessionalProfile = (providerId: string) =>
    router.push({
      pathname: '/[handle]/professional/[providerId]' as any,
      params: { handle: `@${slug}`, providerId },
    });

  // O convidado se identifica depois de montar: avulso abre o sheet de
  // agendamento, recorrência abre o de identidade com a agenda pronta.
  // `serviceId` e não `service.id`: ao trocar de profissional, o detalhe
  // anterior fica na tela até o novo chegar.
  const recurrenceDto = grade.dto;
  const withWhom =
    professionals.length >= 2 && selectedProfessional
      ? ` com ${getFirstName(selectedProfessional.name)}`
      : '';

  // No plano o valor é do ciclo, não da sessão.
  const planPriceLabel =
    service.price == null || service.priceDisplayMode === 'hidden'
      ? null
      : `${formatCurrency(service.price)} ${
          service.recurringTerms?.billingInterval === 'WEEKLY' ? 'por semana' : 'por mês'
        }`;
  // Em "qualquer", o horário sozinho não fecha: falta dizer com quem.
  // Assinatura passa pelo plano mesmo sem repetir: as datas escolhidas entram
  // com ela. Avulso é agendamento direto.
  const asPlan = !!grade.dto;
  const canConfirm = anyMode
    ? false
    : asPlan
      ? !!grade.plan?.valid && !grade.overQuota
      : !!selected;
  const sessionCount = grade.plan?.totalSessions ?? 0;
  const sessionWord = sessionCount === 1 ? 'sessão' : 'sessões';
  const confirmLabel = anyMode
    ? selected
      ? 'Escolha com quem vai ser'
      : 'Selecione um horário'
    : asPlan
      ? grade.plan
        ? isSubscription
          ? `Assinar e deixar marcado · ${sessionCount} ${sessionWord}`
          : `Pedir recorrência · ${sessionCount} ${sessionWord}`
        : 'Montando a agenda…'
      : selected
        ? `Agendar ${formatSlotTime(selected.startAt)}${withWhom}`
        : 'Selecione um horário';

  return (
    <View className="flex-1 bg-neutral-50">
      {meta ? <SeoHead title={meta.title} description={meta.description} path={meta.path} /> : null}
      <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ flexGrow: 1 }}>
        <PublicBrandHeader />

        {/* Seta na mesma linha do bloco de infos; as fotos vêm logo abaixo. */}
        <View className="flex-row items-start gap-2 px-4 pt-4">
          <Pressable
            onPress={goBack}
            hitSlop={12}
            className="-ml-1 mt-0.5 p-1"
            accessibilityLabel="Voltar"
          >
            <Ionicons className="text-neutral-900" name="arrow-back" size={24} />
          </Pressable>

          <View className="flex-1 gap-1">
            <ThemedText role="heading" aria-level={1} className="font-bold text-2xl leading-8">
              {service.name}
            </ThemedText>
            {!anyMode && (
              <View className="flex-row items-center gap-1.5">
                <Ionicons className="text-neutral-500" name="person-circle-outline" size={15} />
                <ThemedText className="font-medium text-sm text-neutral-500">
                  {service.providerName}
                </ThemedText>
              </View>
            )}
            <ThemedText className="mt-1 font-bold text-xl" style={{ color: brand }}>
              {priceLabel}
            </ThemedText>
          </View>
        </View>

        {/* Capa: só existe se o serviço tiver fotos (carrossel de cards). */}
        {hasPhotos && (
          <View className="pt-5">
            <ServiceCoverCarousel photos={service.photos} />
          </View>
        )}

        <View className="gap-4 px-4 pt-6">
          {/* Sobre o serviço */}
          {(!!service.description ||
            !!service.durationMinutes ||
            service.slotCapacity > 1 ||
            !!service.address) && (
            <View className="gap-3 rounded-2xl bg-card p-5" style={{ borderCurve: 'continuous' }}>
              <ThemedText className="font-bold text-base">Sobre o serviço</ThemedText>
              {!!service.description && (
                <ThemedText className="text-base leading-6 text-neutral-600">
                  {service.description}
                </ThemedText>
              )}
              <View className="gap-2.5">
                {!!service.durationMinutes && (
                  <DetailRow
                    icon="time-outline"
                    label="Duração"
                    value={formatDuration(service.durationMinutes)}
                  />
                )}
                {service.slotCapacity > 1 && (
                  <DetailRow
                    icon="people-outline"
                    label="Capacidade"
                    value={`até ${service.slotCapacity} por horário`}
                  />
                )}
                {!!service.address && (
                  <DetailRow icon="location-outline" label="Local" value={service.address} />
                )}
              </View>
            </View>
          )}

          {/* Com quem: trocar recarrega o detalhe e os horários da pessoa */}
          {hasTeam && (
            <View className="gap-3 rounded-2xl bg-card p-5" style={{ borderCurve: 'continuous' }}>
              <View className="flex-row items-center justify-between gap-3">
                <ThemedText className="font-bold text-base">Com quem?</ThemedText>
                {anyMode && (
                  <ThemedText className="text-xs text-neutral-400">
                    ou escolha o horário primeiro
                  </ThemedText>
                )}
              </View>
              <ProfessionalCards
                options={professionals}
                selectedId={selectedProviderId}
                onSelect={chooseProfessional}
                brandColor={brand}
                onOpenProfile={openProfessionalProfile}
                allLabel={isSubscription ? undefined : 'Qualquer'}
              />
            </View>
          )}

          {/* Agendamento */}
          <View className="gap-4 rounded-2xl bg-card p-5" style={{ borderCurve: 'continuous' }}>
            <ThemedText className="font-bold text-base">Escolha um horário</ThemedText>

            {/* Só os termos do plano: assinar é o CTA do fim, com os horários. */}
            {quotaTerms && (
              <SubscriptionPlanCard brand={brand} terms={quotaTerms} priceLabel={planPriceLabel} />
            )}

            {!bookingOn ? (
              <View
                className="items-center gap-2 rounded-2xl bg-neutral-50 p-6"
                style={{ borderCurve: 'continuous' }}
              >
                <Ionicons className="text-neutral-400" name="calendar-clear-outline" size={36} />
                <ThemedText className="text-center font-semibold">
                  Agenda online indisponível
                </ThemedText>
                <ThemedText className="text-center text-sm text-neutral-500">
                  {service.providerName} ainda não abriu a agenda online para este serviço. Volte ao
                  perfil para entrar em contato.
                </ThemedText>
                <PressableScale
                  onPress={() => router.replace(`/@${slug}` as any)}
                  className="mt-2 rounded-full px-5 py-2.5"
                  style={{ backgroundColor: brand }}
                >
                  <ThemedText className="font-bold text-sm text-white">Voltar ao perfil</ThemedText>
                </PressableScale>
              </View>
            ) : availability.isLoading ? (
              <View className="items-center py-8">
                <ActivityIndicator color={brand} />
              </View>
            ) : hasSlots ? (
              <>
                <ScheduleGrade
                  grade={grade}
                  days={days}
                  brand={brand}
                  showCapacity={service.slotCapacity > 1 || anyMode}
                  hint={
                    isSubscription
                      ? 'Este serviço é uma assinatura. Escolher os horários aqui só adianta o trabalho: eles ficam pré-marcados, dentro da sua cota.'
                      : undefined
                  }
                >
                  {/* Horário primeiro: quem está livre nele aparece aqui. */}
                  {anyMode && selected && (
                    <View
                      className="gap-3 rounded-2xl bg-neutral-50 p-4"
                      style={{ borderCurve: 'continuous' }}
                    >
                      <ThemedText className="font-semibold text-sm">
                        Livres às {formatSlotTime(selected.startAt)}
                      </ThemedText>
                      <ProfessionalCards
                        options={freeAtSlot}
                        selectedId={null}
                        onSelect={chooseForSlot}
                        brandColor={brand}
                        onOpenProfile={openProfessionalProfile}
                      />
                    </View>
                  )}
                </ScheduleGrade>

                <PressableScale
                  haptic
                  disabled={!canConfirm}
                  onPress={() => (asPlan ? setRecurrenceOpen(true) : setSheetOpen(true))}
                  className="mt-2 w-full flex-row items-center justify-center gap-2 rounded-full py-4"
                  style={{
                    backgroundColor: canConfirm ? brand : th('#D4D4D4'),
                    borderCurve: 'continuous',
                    boxShadow: canConfirm ? '0 6px 20px rgba(23,17,10,0.22)' : undefined,
                  }}
                >
                  <Ionicons name="checkmark-circle" size={20} color="#fff" />
                  <ThemedText className="font-bold text-base text-white">{confirmLabel}</ThemedText>
                </PressableScale>
              </>
            ) : (
              <View
                className="items-center gap-2 rounded-2xl bg-neutral-50 p-6"
                style={{ borderCurve: 'continuous' }}
              >
                <Ionicons className="text-neutral-400" name="calendar-clear-outline" size={36} />
                <ThemedText className="text-center font-semibold">
                  Sem horários no período
                </ThemedText>
                <ThemedText className="text-center text-sm text-neutral-500">
                  Não há vagas nos próximos {HORIZON_DAYS} dias. Tente novamente mais tarde.
                </ThemedText>
              </View>
            )}
          </View>
        </View>

        <View className="mt-16 flex-1" />
        <PublicBrandFooter reportSlug={slug} />
      </ScrollView>

      <GuestRecurrenceSheet
        askAddress={askAddress}
        open={recurrenceOpen}
        onClose={() => {
          setRecurrenceOpen(false);
          grade.clear();
          void availability.refetch();
        }}
        onSuccess={(result) => {
          setRecurrenceOpen(false);
          grade.clear();
          // Pedido feito: confirmação própria (aguardando o prestador + conta),
          // e dali os agendamentos. replace: voltar não reabre o sheet.
          const first = result.plan.occurrences.find((o) => o.available);
          router.replace({
            pathname: '/agendamento/recorrencia-enviada',
            params: {
              subscription: isSubscription ? '1' : '0',
              sessions: String(result.plan.totalSessions),
              ...(first ? { first: first.startAt } : {}),
              ...(service?.name ? { service: service.name } : {}),
              claimable: result.accountClaimable ? '1' : '0',
              slug,
              brand,
            },
          } as never);
        }}
        slug={slug}
        professionalId={professionalId}
        brand={brand}
        dto={recurrenceDto}
        subscription={isSubscription}
      />

      <GuestBookingSheet
        askAddress={askAddress}
        open={sheetOpen}
        onClose={() => {
          setSheetOpen(false);
          void availability.refetch();
        }}
        slug={slug}
        professionalId={professionalId}
        serviceId={serviceId}
        slot={selected}
        brandColor={brand}
        onSuccess={(result) => {
          setSheetOpen(false);
          // replace: o voltar não pode cair de novo no sheet de agendamento.
          router.replace({
            pathname: '/agendamento/[appointmentId]/sucesso',
            params: {
              appointmentId: result.appointment.id,
              token: result.magicToken,
              slug,
              brand,
            },
          } as never);
        }}
      />
    </View>
  );
}
